// 0130 · EXCEPCIONES DE CIERRE (CASH_CLOSING_EXCEPTION). Con política de cierre en la caja, nadie
// revisa todos los cierres: solo las excepciones. V3 (2026-10-30), por ROLES ASIGNADOS (el «todo» del
// administrador no hace a nadie revisor):
//  - nivel 1 · soporte comercial (principal) o, si el principal participó en el turno o no hay, la cola
//    alterna (tesorería o administración de cajas). Resuelven solo dentro de la política; si no, escalan;
//  - nivel 2 · aprobación superior; si participó, la alterna de nivel 2 (administración de cajas); si
//    nadie queda sin conflicto, OPEN_REQUIRES_INDEPENDENT_REVIEW: abierta, visible, avisada por rol y sin
//    autoaprobación.
// V4: las señales se muestran como señales (no son fraude); quien ejecutó la anulación o la corrección
// que causó la excepción no la revisa; la corrección autorizada pide quién la aprobó y una referencia.
// Se resuelve de un clic: un código de causa y un resultado; texto solo para «Otra» o lo que pida la
// política de la caja. Escalar: motivo de un clic, nota solo con «Otro». Quien participó en el turno no
// ve «resolver»; lo que cada persona puede hacer lo dice el servidor (`acciones`).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { claves, useExcepcionCierre, useExcepcionesCierre } from '../api/consultas';
import type {
  CodigoCausa,
  ColaRevision,
  ExcepcionCierreDetalle,
  MotivoEscalamiento,
  ResolucionExcepcion,
} from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { AuditoriaTurno } from './AuditoriaTurno';
import { TablaArqueo } from './MiTurno';
import {
  motivosEnTexto,
  NOMBRE_CAUSA,
  NOMBRE_CAUSA_ANULACION,
  NOMBRE_SENAL,
  NOMBRE_ESCALAMIENTO,
  NOMBRE_RESOLUCION,
  nombreCola,
} from './textos';

const FILTROS: readonly {
  clave: string;
  nombre: string;
  estado: 'ABIERTA' | 'RESUELTA';
  nivel?: 1 | 2;
  cola?: ColaRevision;
}[] = [
  { clave: 'soporte', nombre: 'Nivel 1 · soporte', estado: 'ABIERTA', nivel: 1, cola: 'PRINCIPAL' },
  {
    clave: 'alterno1',
    nombre: 'Nivel 1 · tesorería / adm. de cajas',
    estado: 'ABIERTA',
    nivel: 1,
    cola: 'ALTERNO',
  },
  {
    clave: 'n2',
    nombre: 'Nivel 2 · aprobación superior',
    estado: 'ABIERTA',
    nivel: 2,
    cola: 'PRINCIPAL',
  },
  { clave: 'alterno2', nombre: 'Nivel 2 · alterna', estado: 'ABIERTA', nivel: 2, cola: 'ALTERNO' },
  {
    clave: 'independiente',
    nombre: 'Revisión independiente',
    estado: 'ABIERTA',
    nivel: 2,
    cola: 'OPEN_REQUIRES_INDEPENDENT_REVIEW',
  },
  { clave: 'resueltas', nombre: 'Resueltas', estado: 'RESUELTA' },
];

export function ExcepcionesCierre({ activa }: { activa: boolean }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  // Cada revisor parte por su cola (la pantalla solo filtra: lo que puede hacer lo dice el servidor).
  const inicial = puede('caja.excepcion.resolver_nivel_dos')
    ? 'n2'
    : puede('caja.excepcion.resolver')
      ? 'soporte'
      : puede('caja.excepcion.resolver_tesoreria')
        ? 'alterno1'
        : 'alterno2';
  const [filtro, setFiltro] = useState<string>(inicial);
  const f = FILTROS.find((x) => x.clave === filtro)!;
  const lista = useExcepcionesCierre(
    empresaId,
    {
      estado: f.estado,
      ...(f.nivel ? { nivel: f.nivel } : {}),
      ...(f.cola ? { cola: f.cola } : {}),
    },
    activa,
  );
  // V4 · la alerta: excepciones de nivel 2 sin nadie libre de conflicto (siguen abiertas y avisadas).
  const independientes = useExcepcionesCierre(
    empresaId,
    { estado: 'ABIERTA', nivel: 2, cola: 'OPEN_REQUIRES_INDEPENDENT_REVIEW' },
    activa,
  );
  const alertas = (independientes.data ?? []).filter((x) => x.requiereRevisionIndependiente).length;
  const [elegida, setElegida] = useState<string | null>(null);
  return (
    <div className="pantalla consulta caja-compartida" data-pantalla="excepciones-caja">
      <h2>Excepciones de cierre de caja</h2>
      <p className="apagado">
        Solo los cierres con diferencia o incidentes de las cajas con política de cierre, y las
        anulaciones o correcciones hechas después del cierre. Un cierre que cuadra no llega aquí.
      </p>
      {alertas > 0 && (
        <p className="aviso-linea tono-aviso" role="alert">
          {alertas} excepción(es) requieren revisión independiente: nadie con rol de nivel 2 está
          libre de conflicto de interés. Siguen abiertas, se avisó por rol y nadie las autoaprueba.
        </p>
      )}
      <div className="acciones" role="group" aria-label="Filtro de excepciones">
        {FILTROS.map((x) => (
          <button
            key={x.clave}
            type="button"
            aria-pressed={filtro === x.clave}
            className={filtro === x.clave ? 'primario' : undefined}
            onClick={() => {
              setFiltro(x.clave);
              setElegida(null);
            }}
          >
            {x.nombre}
          </button>
        ))}
      </div>
      {lista.isError && <PanelError error={lista.error} />}
      <table className="grilla compacta" aria-label="Excepciones de cierre">
        <thead>
          <tr>
            <th>Turno</th>
            <th>Caja</th>
            <th>Responsable</th>
            <th>Cerrado</th>
            <th>Motivos</th>
            <th className="al-der">Diferencia efectivo</th>
            <th>Nivel</th>
          </tr>
        </thead>
        <tbody>
          {(lista.data ?? []).map((x) => (
            <tr
              key={x.excepcionId}
              aria-selected={elegida === x.excepcionId}
              className={elegida === x.excepcionId ? 'seleccionada' : undefined}
            >
              <td>
                <button
                  type="button"
                  className="enlace mono"
                  onClick={() => setElegida(x.excepcionId)}
                >
                  {x.turno}
                </button>
              </td>
              <td>{x.caja}</td>
              <td>{x.responsable.nombre}</td>
              <td>{x.cerradoEn ? fechaHora(x.cerradoEn) : '—'}</td>
              <td>{motivosEnTexto(x.motivos)}</td>
              <td className="al-der">{pesos(x.diferenciaEfectivo)}</td>
              <td>
                {x.nivel}
                {x.cola ? ` · ${nombreCola(x.nivel, x.cola)}` : ''}
                {x.nivelInicial !== x.nivel ? ' (escalada)' : ''}
              </td>
            </tr>
          ))}
          {lista.isSuccess && !lista.data.length && (
            <tr>
              <td colSpan={7} className="grilla-vacia">
                No hay excepciones · {f.nombre.toLowerCase()}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {elegida && <DetalleExcepcion empresaId={empresaId} excepcionId={elegida} />}
    </div>
  );
}

/** Una reincidencia (por persona o por caja) en palabras: previas, umbral, ventana y si se alcanzó. */
function textoReincidencia(r: unknown): string | null {
  const x = r as
    { previas?: number; umbral?: number; dias?: number; alcanzada?: boolean } | undefined;
  if (!x || x.previas === undefined) return null;
  return `${x.previas} previa(s)${x.umbral ? ` · umbral ${x.umbral}` : ''}${x.dias ? ` en ${x.dias} días` : ''}${x.alcanzada ? ' · alcanzada (nivel 2)' : ''}`;
}

function DetalleExcepcion({ empresaId, excepcionId }: { empresaId: string; excepcionId: string }) {
  const q = useExcepcionCierre(empresaId, excepcionId);
  const [dialogo, setDialogo] = useState<null | 'resolver' | 'escalar' | 'auditoria'>(null);
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return null;
  const x = q.data;
  const acciones = x.acciones ?? { resolver: false, escalar: false, razon: null };
  const reinc = x.reincidencias ?? {};
  const porPersona = textoReincidencia(reinc['operador']);
  const porCaja = textoReincidencia(reinc['caja']);
  const responsable = reinc['responsable'] as { previas?: number } | undefined;
  return (
    <section className="caja-detalle" aria-label={`Excepción del turno ${x.turno}`}>
      <h3>
        {x.turno} · {x.caja} · responsable {x.responsable.nombre} · nivel {x.nivel}
        {x.cola ? ` (${nombreCola(x.nivel, x.cola)})` : ''}
        {x.origen === 'POST_CLOSE_VOID_EXCEPTION'
          ? ' · anulada después del cierre (POST_CLOSE_VOID_EXCEPTION)'
          : ''}
        {x.origen === 'CORRECCION_POSTERIOR' ? ' · medios corregidos después del cierre' : ''}
      </h3>
      {x.legado && (
        <p className="aviso-linea" role="note">
          Turno legado (LEGACY_SHARED): lo abrió una cuenta compartida de puesto y la persona
          responsable es desconocida. Rigen las reglas de la caja (no las personales).
        </p>
      )}
      {x.requiereRevisionIndependiente && (
        <p className="aviso-linea tono-aviso" role="alert">
          Nadie con rol de nivel 2 queda sin conflicto de interés: la excepción sigue abierta y
          nadie la autoaprueba.
        </p>
      )}
      <p>
        Motivos: <b>{motivosEnTexto(x.motivos)}</b>
        {x.anulaciones ? ` · ${x.anulaciones} cobro(s) anulado(s)` : ''}
        {x.brechasRuta ? ` · ${x.brechasRuta} brecha(s) de plata de ruta` : ''}
        {x.observacionCierre ? ` · «${x.observacionCierre}»` : ''}
      </p>
      {!!x.participantes?.length && (
        <p>Participaron (no la resuelven): {x.participantes.map((p) => p.nombre).join(', ')}</p>
      )}
      {!!x.actores?.length && (
        <p>
          Ejecutaron la anulación o la corrección (no revisan esta excepción, pero no participaron
          del turno): {x.actores.map((p) => p.nombre).join(', ')}
        </p>
      )}
      {!!x.senales?.length && (
        <section className="recuadro" aria-label="Señales">
          <h4>Señales (no son fraude: avisos para mirar)</h4>
          <ul className="lista-compacta">
            {x.senales.map((s, i) => (
              <li key={`${s.codigo}-${i}`} data-senal={s.codigo} data-escala={s.escala}>
                {NOMBRE_SENAL[s.codigo] ?? s.codigo}
                {s.escala ? ' · sube a nivel 2' : ' · informativa'}
              </li>
            ))}
          </ul>
        </section>
      )}
      {(porPersona || porCaja) && (
        <table className="grilla compacta" aria-label="Reincidencias">
          <thead>
            <tr>
              <th>Reincidencia</th>
              <th>Medida</th>
            </tr>
          </thead>
          <tbody>
            <tr data-reincidencia="persona">
              <td>Por persona</td>
              <td>{porPersona ?? '—'}</td>
            </tr>
            <tr data-reincidencia="caja">
              <td>Por caja</td>
              <td>{porCaja ?? '—'}</td>
            </tr>
            {responsable?.previas !== undefined && (
              <tr data-reincidencia="responsable">
                <td>De la responsable (solo métrica)</td>
                <td>{responsable.previas} previa(s)</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {!!x.anulacionesDetalle?.length && (
        <table className="grilla compacta" aria-label="Cobros anulados">
          <thead>
            <tr>
              <th>Recibo</th>
              <th className="al-der">Monto</th>
              <th>Pidió</th>
              <th>Ejecutó</th>
              <th>Nivel</th>
              <th>Por qué</th>
            </tr>
          </thead>
          <tbody>
            {x.anulacionesDetalle.map((a) => (
              <tr key={a.reciboId} data-nivel-anulacion={a.nivel ?? ''}>
                <td className="mono">{a.numero}</td>
                <td className="al-der">{pesos(a.monto)}</td>
                <td>
                  {a.tipo === 'CORRECCION' ? '—' : (a.solicitadoPorNombre ?? 'sin solicitud')}
                  {a.causaSolicitud ? ` · ${NOMBRE_CAUSA[a.causaSolicitud]}` : ''}
                </td>
                <td>
                  {a.tipo === 'CORRECCION'
                    ? `corrigió ${a.corregidoPorNombre ?? '—'}`
                    : (a.anuladoPorNombre ?? '—')}
                </td>
                <td>{a.nivel ?? x.nivel}</td>
                <td>
                  {a.tipo === 'CORRECCION'
                    ? a.causa
                      ? NOMBRE_CAUSA[a.causa]
                      : 'sin causa'
                    : a.causas?.length
                      ? a.causas.map((c) => NOMBRE_CAUSA_ANULACION[c] ?? c).join(', ')
                      : 'dentro de la política'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <TablaArqueo arqueo={x.arqueo} />
      {x.escalamiento && (
        <p>
          Escalada por {x.escalamiento.por} ({fechaHora(x.escalamiento.en)})
          {x.escalamiento.motivo ? ` · ${NOMBRE_ESCALAMIENTO[x.escalamiento.motivo]}` : ''}
          {x.escalamiento.nota ? ` · «${x.escalamiento.nota}»` : ''}
        </p>
      )}
      {x.resolucion && (
        <p data-resolucion={x.resolucion.resolucion}>
          Resuelta por {x.resolucion.por} ({fechaHora(x.resolucion.en)}):{' '}
          {x.resolucion.causa ? <b>{NOMBRE_CAUSA[x.resolucion.causa]} · </b> : null}
          {x.resolucion.autorizacion
            ? `aprobó ${x.resolucion.autorizacion.por} (${x.resolucion.autorizacion.referencia ?? '—'}) · `
            : null}
          <b>{NOMBRE_RESOLUCION[x.resolucion.resolucion]}</b>
          {x.resolucion.nota ? ` · «${x.resolucion.nota}»` : ''}
        </p>
      )}
      {x.estado === 'ABIERTA' && !acciones.resolver && acciones.razon && (
        <p className="aviso-linea" role="note">
          {acciones.razon}
        </p>
      )}
      <footer className="acciones">
        <button type="button" onClick={() => setDialogo('auditoria')}>
          Auditoría del turno
        </button>
        {acciones.escalar && (
          <button type="button" onClick={() => setDialogo('escalar')}>
            Escalar a nivel 2…
          </button>
        )}
        {acciones.resolver && (
          <button type="button" className="primario" onClick={() => setDialogo('resolver')}>
            Resolver…
          </button>
        )}
      </footer>
      {dialogo === 'auditoria' && (
        <AuditoriaTurno
          empresaId={empresaId}
          turnoId={x.turnoId}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {(dialogo === 'resolver' || dialogo === 'escalar') && (
        <AccionExcepcion
          empresaId={empresaId}
          excepcion={x}
          accion={dialogo}
          alCerrar={() => setDialogo(null)}
        />
      )}
    </section>
  );
}

function AccionExcepcion({
  empresaId,
  excepcion,
  accion,
  alCerrar,
}: {
  empresaId: string;
  excepcion: ExcepcionCierreDetalle;
  accion: 'resolver' | 'escalar';
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const nivelUno = excepcion.nivel === 1;
  // Un clic cada uno: nada viene marcado, para que la elección sea explícita.
  const [causa, setCausa] = useState<CodigoCausa | null>(null);
  const [resolucion, setResolucion] = useState<ResolucionExcepcion | null>(null);
  const [motivo, setMotivo] = useState<MotivoEscalamiento | null>(null);
  const [nota, setNota] = useState('');
  // V4: la corrección autorizada pide quién la aprobó o pidió y una referencia (no texto).
  const [autorizadaPor, setAutorizadaPor] = useState('');
  const [referencia, setReferencia] = useState('');
  const autorizada = accion === 'resolver' && causa === 'AUTHORIZED_CORRECTION';
  // El nivel 1 solo resuelve dentro de la política (sin «Otra»).
  const resoluciones = (Object.keys(NOMBRE_RESOLUCION) as ResolucionExcepcion[]).filter(
    (r) => !nivelUno || r !== 'OTRA',
  );
  // Texto solo si el código lo exige: «Otra» siempre, o lo que liste la política de la caja.
  const conTexto =
    accion === 'resolver'
      ? causa === 'OTHER' || (!!causa && excepcion.politica.codigosConTexto?.includes(causa))
      : motivo === 'OTRO';
  const faltaTexto = conTexto && !nota.trim();
  const falta =
    (accion === 'resolver' ? !causa || !resolucion : !motivo) ||
    faltaTexto ||
    (autorizada && (!autorizadaPor || !referencia.trim()));
  const enviar = useMutation({
    mutationFn: () =>
      api.post(
        `/caja/excepciones/${excepcion.excepcionId}/${accion}`,
        accion === 'resolver'
          ? {
              causa,
              resolucion,
              ...(conTexto && nota.trim() ? { nota: nota.trim() } : {}),
              ...(autorizada ? { autorizadaPor, autorizacionReferencia: referencia.trim() } : {}),
            }
          : { motivo, ...(conTexto && nota.trim() ? { nota: nota.trim() } : {}) },
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: claves.caja(empresaId) });
      alCerrar();
    },
  });
  const titulo = accion === 'resolver' ? 'Resolver excepción' : 'Escalar a nivel 2';
  return (
    <Modal
      titulo={`${titulo} · ${excepcion.turno}`}
      ancho="angosto"
      alCerrar={() => !enviar.isPending && alCerrar()}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={enviar.isPending}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={enviar.isPending || falta}
            onClick={() => enviar.mutate()}
          >
            {enviar.isPending ? 'Guardando…' : titulo}
          </button>
        </>
      }
    >
      {accion === 'resolver' ? (
        <>
          <fieldset className="campo caja-revision">
            <legend className="etiqueta">Causa</legend>
            {(Object.keys(NOMBRE_CAUSA) as CodigoCausa[]).map((c) => (
              <label key={c}>
                <input
                  type="radio"
                  name="causa"
                  checked={causa === c}
                  onChange={() => setCausa(c)}
                />{' '}
                {NOMBRE_CAUSA[c]}
              </label>
            ))}
          </fieldset>
          <fieldset className="campo caja-revision">
            <legend className="etiqueta">Resultado</legend>
            {resoluciones.map((r) => (
              <label key={r}>
                <input
                  type="radio"
                  name="resolucion"
                  checked={resolucion === r}
                  onChange={() => setResolucion(r)}
                />{' '}
                {NOMBRE_RESOLUCION[r]}
              </label>
            ))}
          </fieldset>
        </>
      ) : (
        <fieldset className="campo caja-revision">
          <legend className="etiqueta">Por qué se escala</legend>
          {(Object.keys(NOMBRE_ESCALAMIENTO) as MotivoEscalamiento[]).map((m) => (
            <label key={m}>
              <input
                type="radio"
                name="motivo-escalamiento"
                checked={motivo === m}
                onChange={() => setMotivo(m)}
              />{' '}
              {NOMBRE_ESCALAMIENTO[m]}
            </label>
          ))}
        </fieldset>
      )}
      {autorizada && (
        <fieldset className="campo">
          <legend className="etiqueta">Corrección autorizada</legend>
          <label className="campo">
            <span className="etiqueta">Quién la aprobó o pidió</span>
            <select
              aria-label="Quién la aprobó o pidió"
              value={autorizadaPor}
              onChange={(e) => setAutorizadaPor(e.target.value)}
            >
              <option value="">— elegir —</option>
              {(excepcion.autorizadores ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">Referencia (solicitud, firma o auditoría)</span>
            <input
              aria-label="Referencia de la autorización"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </label>
        </fieldset>
      )}
      {conTexto && (
        <label className="campo">
          <span className="etiqueta">Qué pasó (obligatorio para esta opción)</span>
          <input
            aria-label="Nota de la excepción"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
          />
        </label>
      )}
      {enviar.isError && <PanelError error={enviar.error} />}
    </Modal>
  );
}
